import { Box, Button, useTheme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { RequestFormContext } from "../RequestFormContext";
import { ArrowRightIcon } from "vig-ui-library";
import { RequestFormView } from "../Enums/RequestFormView";
import { RequestStateTypeEnum } from "../Enums/RequestStateTypeEnum";
import { CreatedRequestOutput } from "../Schemas/CreatedRequestOutput";
import { SignatureMethod } from "../Enums/SignatureMethod";
import { MagicLinkIdCardUploadMethod } from "../Enums/MagicLinkIdCardUploadMethod";
import { signatureFlowContentGutterSx } from "../Utilities/signatureFlowLayout";

export type SignatureFlowFootersProps = Omit<RequestFormContext, "pathToProp">;

export const SignatureFlowFooters = (props: SignatureFlowFootersProps) => {
    const theme = useTheme();
    const {
        selectedSignatureMethod,
        magicLinkUploadMethod,
        request,
        saveRequestAndSubmit,
        downloadRequestDocument,
        isSaving,
        settings,
        saveTask,
        submitTask,
        getExistingRequestDataOnBackgroundTask,
        openSnackbar,
        setMagicLinkError,
        setView,
        startMagicLinkProcessTask,
        cancelMagicLinkProcessTask,
        createMagicLinkTask,
        loadMagicLinkConfigTask,
        view,
    } = props;

    const isManualOnlySignature = (settings.current.signatureMethodCount ?? 0) < 2;

    const handleEditRequest = async () => {
        if (view === RequestFormView.UPLOAD_ID_CARD) {
            // Manual sign never starts a magic-link process, so leaving the upload view must not cancel one.
            if (selectedSignatureMethod !== SignatureMethod.Manual) {
                try {
                    await cancelMagicLinkProcessTask.perform();
                } catch (error) {
                    // If cancel fails, still allow user to go back
                    console.error("Failed to cancel magic link process:", error);
                }
            }

            if (selectedSignatureMethod === SignatureMethod.Manual && isManualOnlySignature) {
                setView(RequestFormView.FORM);
            } else {
                setView(RequestFormView.SIGNATURE_METHOD_SELECTION);
            }
        } else {
            setView(RequestFormView.FORM);
        }
    };

    const allowSubmit = (uploadProcesses: boolean[]) => {
        return new Promise<void>((resolve, reject) => {
            if (uploadProcesses && uploadProcesses.length > 0) {
                openSnackbar({ text: "Prosím najprv počkajte, kým sa ukončí nahrávanie súborov" });
                reject();
            } else {
                resolve();
            }
        });
    };

    const submitManualSignature = () => {
        const currentRequest = request.current;
        const requestsStates = currentRequest.requestState || [];
        const lastRequestState = requestsStates.length > 0 ? requestsStates[requestsStates.length - 1] : null;

        const isSentForSign = settings.current.previewMode &&
            !currentRequest.isSubmitted &&
            lastRequestState?.requestStateTypeId === RequestStateTypeEnum.ODOSLANA_NA_PODPIS;

        if (isSentForSign && settings.current.manualSignRequired && !(currentRequest.ismanuallySigned ?? false)) {
            downloadRequestDocument();
            setView(RequestFormView.FORM);
            return;
        }

        settings.current.previewMode = true;

        if (settings.current.manualSignRequired === true && !(currentRequest.ismanuallySigned ?? false)) {
            const params = {
                successHandler: async () => {
                    const submitParams = {
                        successHandler: async (_result: CreatedRequestOutput) => {
                            await getExistingRequestDataOnBackgroundTask.perform();
                            downloadRequestDocument();
                            setView(RequestFormView.FORM);
                        }
                    };

                    await allowSubmit(settings.current.uploadProcesses);
                    submitTask.perform(submitParams);
                }
            };
            saveTask.perform(params);
        } else {
            saveRequestAndSubmit(false, currentRequest.ismanuallySigned);
        }
    };

    const manualSignNeedsSeparateIdCardUpload = async (): Promise<boolean | null> => {
        if (props.idCardFrontTaxonomyId.current != null && props.idCardBackTaxonomyId.current != null) {
            return !props.isIdCardUploadRequiredForRequest;
        }

        const config = await loadMagicLinkConfigTask.perform();
        if (!config) {
            return null;
        }

        return !config.isIdCardUploadRequiredForRequest;
    };

    const handleRightButtonClick = async () => {
        if (view === RequestFormView.UPLOAD_ID_CARD) {
            if (selectedSignatureMethod === SignatureMethod.Manual) {
                submitManualSignature();
                return;
            }

            setMagicLinkError(null);
            createMagicLinkTask.perform();
            return;
        }

        if (selectedSignatureMethod === SignatureMethod.Manual) {
            const currentRequest = request.current;
            const requestsStates = currentRequest.requestState || [];
            const lastRequestState = requestsStates.length > 0 ? requestsStates[requestsStates.length - 1] : null;

            const isSentForSign = settings.current.previewMode &&
                !currentRequest.isSubmitted &&
                lastRequestState?.requestStateTypeId === RequestStateTypeEnum.ODOSLANA_NA_PODPIS;

            if (isSentForSign && settings.current.manualSignRequired && !(currentRequest.ismanuallySigned ?? false)) {
                downloadRequestDocument();
                setView(RequestFormView.FORM);
                return;
            }

            const needsIdCardUpload = await manualSignNeedsSeparateIdCardUpload();
            if (needsIdCardUpload === null) {
                return;
            }

            if (needsIdCardUpload) {
                setView(RequestFormView.UPLOAD_ID_CARD);
                return;
            }

            submitManualSignature();
        } else if (selectedSignatureMethod === SignatureMethod.MagicLink) {
            // Handle magic link sending
            // If ID card upload is required for request, always send link (ID card will be uploaded in form)
            if (!props.isIdCardUploadRequiredForRequest && magicLinkUploadMethod === MagicLinkIdCardUploadMethod.You) {
                // Start magic link process and navigate to upload ID card view
                setMagicLinkError(null);
                await startMagicLinkProcessTask.perform();
                setView(RequestFormView.UPLOAD_ID_CARD);
            } else {
                // Send magic link
                setMagicLinkError(null);
                createMagicLinkTask.perform();
            }
        }
    };

    const getRightButtonLabel = () => {
        if (selectedSignatureMethod === SignatureMethod.Manual) {
            return "Podpísať žiadosť";
        }
        return "Odoslať link";
    };

    const isRightButtonDisabled = () => {
        // For UPLOAD_ID_CARD view, enable button only when both sides are uploaded
        if (view === RequestFormView.UPLOAD_ID_CARD) {
            const hasFrontFile = props.getIdCardFrontFileId() !== null;
            const hasBackFile = props.getIdCardBackFileId() !== null;
            return !hasFrontFile || !hasBackFile || isSaving || createMagicLinkTask.isRunning || loadMagicLinkConfigTask.isRunning;
        }
        return !selectedSignatureMethod || isSaving || createMagicLinkTask.isRunning || startMagicLinkProcessTask.isRunning || loadMagicLinkConfigTask.isRunning;
    };

    const continueDisabled = isRightButtonDisabled();
    const continueArrowColor = continueDisabled
        ? theme.palette.text.disabled
        : theme.palette.common.white;

    const continueIcon = (
        <ArrowRightIcon width={18} height={18} color={continueArrowColor} />
    );

    return (
        <Box
            sx={{
                ...signatureFlowContentGutterSx,
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1
            }}
        >
            <Button
                variant="text"
                size="large"
                startIcon={
                    <Box sx={{ display: "flex", transform: "rotate(180deg)" }}>
                        <ArrowRightIcon width={18} height={18} color={theme.palette.text.secondary} />
                    </Box>
                }
                onClick={handleEditRequest}
                disabled={isSaving}
                sx={{
                    textTransform: "none",
                    color: theme.palette.text.secondary,
                    "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.primary,
                    },
                }}
            >
                {view === RequestFormView.UPLOAD_ID_CARD && !(selectedSignatureMethod === SignatureMethod.Manual && isManualOnlySignature)
                    ? "Zmeniť spôsob podpisu"
                    : "Upraviť žiadosť"}
            </Button>

            <LoadingButton
                size="large"
                variant="contained"
                color="primary"
                endIcon={continueIcon}
                loadingPosition="end"
                onClick={handleRightButtonClick}
                disabled={isRightButtonDisabled()}
                loading={isSaving || createMagicLinkTask.isRunning || startMagicLinkProcessTask.isRunning || loadMagicLinkConfigTask.isRunning}
                sx={{ minWidth: { xs: "min(100%, 220px)", sm: 220 } }}
            >
                {getRightButtonLabel()}
            </LoadingButton>
        </Box>
    );
};

